import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Camera, Megaphone, Monitor, Compass } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useSiteContent } from "../context/SiteContentContext";
import { ROUTES, servicePath } from "@/lib/routes";
import PageHero from "../components/PageHero";
import LandscapeCta from "../components/LandscapeCta";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: i * 0.07 },
  }),
};

const iconMap = {
  "social-media-management": Camera,
  ads: Megaphone,
  website: Monitor,
  consultanta: Compass,
};

export default function ServicesPage() {
  const { t, lang, serviceCards } = useLanguage();
  const { mergedServicePages } = useSiteContent();
  const menu = serviceCards;
  const pages = mergedServicePages[lang === "en" ? "en" : "ro"] || mergedServicePages.ro;
  const pointsRaw =
    Array.isArray(t?.services_page?.trust_points) && t.services_page.trust_points.length
      ? t.services_page.trust_points
      : lang === "en"
        ? ["Strategy before execution", "Dedicated team, no outsourcing", "Transparent reporting"]
        : ["Strategie înainte de execuție", "Echipă dedicată, fără outsourcing", "Raportare transparentă"];
  const points = pointsRaw
    .map((p) => (typeof p === "string" ? p : p?.value))
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean);

  return (
    <main className="bg-white" data-testid="services-page">
      <PageHero
        variant="dark"
        eyebrow={t.services_page.eyebrow}
        title={t.services_page.title}
        description={t.services_page.subtitle}
        testId="services-hero"
      />

      {/* Trust strip */}
      <section className="border-b border-[#3e2723]/8 bg-[#faf8f8] py-8 sm:py-10">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-4 px-6 sm:px-12 lg:px-20">
          {points.map((point, i) => (
            <div key={i} className="flex items-center gap-2.5 text-sm font-medium text-[#3e2723]/75">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#fbd9e5] text-[11px] font-bold text-[#3e2723]">
                {String(i + 1).padStart(2, "0")}
              </span>
              {point}
            </div>
          ))}
        </div>
      </section>

      {/* Service bento grid */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            className="mb-10 max-w-2xl sm:mb-14"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#3e2723]/45">
              {t.services_page.expertise_kicker || (lang === "en" ? "01 · Expertise" : "01 · Expertiză")}
            </p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tighter text-[#3e2723] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.05]">
              {t.services_page.expertise_title ||
                (lang === "en" ? "Four pillars. One growth engine." : "Patru piloni. Un singur motor de creștere.")}
            </h2>
          </motion.div>

          <div className="grid gap-4 md:grid-cols-2 lg:gap-5">
            {menu.map((s, i) => {
              const Icon = iconMap[s.slug] || Camera;
              const page = pages[s.slug];
              const isDark = i === 1 || i === 2;

              return (
                <motion.div
                  key={s.slug}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  className=""
                >
                  <Link
                    to={servicePath(s.slug)}
                    className={`group relative flex h-full min-h-[280px] flex-col overflow-hidden rounded-[1.75rem] border p-7 transition duration-500 hover:-translate-y-1 sm:rounded-[2rem] sm:p-8 lg:min-h-[320px] ${
                      isDark
                        ? "border-[#3e2723]/15 bg-[#3e2723] text-[#fbd9e5] shadow-[0_24px_60px_-24px_rgba(62,39,35,0.45)]"
                        : "border-[#3e2723]/8 bg-white shadow-[0_16px_48px_rgba(62,39,35,0.06)] hover:shadow-[0_24px_60px_rgba(62,39,35,0.1)]"
                    }`}
                    data-testid={`service-tile-${s.slug}`}
                  >
                    <div
                      aria-hidden
                      className={`pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full blur-3xl transition-opacity ${
                        isDark ? "bg-[#fbd9e5]/15 group-hover:bg-[#fbd9e5]/25" : "bg-[#fbd9e5]/50 group-hover:bg-[#fbd9e5]/70"
                      }`}
                    />
                    <div className="relative flex flex-1 flex-col">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <span
                            className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${
                              isDark ? "bg-[#fbd9e5]/15 text-[#fbd9e5]" : "bg-[#fbd9e5] text-[#3e2723]"
                            }`}
                          >
                            <Icon className="h-5 w-5" />
                          </span>
                          <span className={`font-display text-sm font-bold tabular-nums ${isDark ? "text-[#fbd9e5]/35" : "text-[#3e2723]/25"}`}>
                            {String(i + 1).padStart(2, "0")}
                          </span>
                        </div>
                        <span
                          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-all duration-300 group-hover:rotate-45 ${
                            isDark
                              ? "border-[#fbd9e5]/20 bg-[#fbd9e5]/10 text-[#fbd9e5] group-hover:bg-[#fbd9e5] group-hover:text-[#3e2723]"
                              : "border-[#3e2723]/10 bg-[#faf8f8] text-[#3e2723] group-hover:bg-[#3e2723] group-hover:text-[#fbd9e5]"
                          }`}
                        >
                          <ArrowUpRight className="h-4 w-4" />
                        </span>
                      </div>

                      <h2
                        className={`mt-8 font-display text-2xl font-bold leading-tight tracking-tight sm:text-[1.65rem] ${
                          isDark ? "text-[#fbd9e5]" : "text-[#3e2723]"
                        }`}
                      >
                        {s.label}
                      </h2>
                      <p className={`mt-3 max-w-md text-[14px] leading-relaxed sm:text-[15px] ${isDark ? "text-[#fbd9e5]/75" : "text-[#3e2723]/70"}`}>
                        {s.desc}
                      </p>
                      {((typeof s.cardLead === "string" && s.cardLead.trim()) || page?.lead) ? (
                        <p
                          className={`mt-auto pt-6 text-[13px] leading-relaxed ${
                            isDark ? "text-[#fbd9e5]/55" : "text-[#3e2723]/50"
                          }`}
                        >
                          {(typeof s.cardLead === "string" && s.cardLead.trim()) ? s.cardLead.trim() : page.lead}
                        </p>
                      ) : null}
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed packages */}
      {Array.isArray(t?.services_page?.detailed) && t.services_page.detailed.length ? (
        <section className="bg-[#faf8f8] py-16 sm:py-24 lg:py-28" data-testid="services-detailed">
          <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
            <div className="grid gap-4 md:grid-cols-2 lg:gap-5">
              {t.services_page.detailed.map((block, i) => {
                if (!block || typeof block !== "object") return null;
                const num = String(block.num || String(i + 1).padStart(2, "0"));
                const features =
                  typeof block.featuresText === "string" && block.featuresText.trim()
                    ? block.featuresText
                        .split(/\r?\n/)
                        .map((x) => x.trim())
                        .filter(Boolean)
                    : Array.isArray(block.features)
                      ? block.features.filter(Boolean)
                      : [];

                return (
                  <motion.article
                    key={`${num}-${i}`}
                    variants={fadeUp}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    className="relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-[#3e2723]/10 bg-white p-7 shadow-[0_16px_48px_rgba(62,39,35,0.06)] sm:p-8"
                    data-testid={`services-detailed-${i}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#3e2723]/45">
                          {num}
                        </p>
                        <h3 className="mt-3 font-display text-2xl font-black tracking-tight text-[#3e2723]">
                          {block.title}
                        </h3>
                        {block.intro ? (
                          <p className="mt-3 text-[14px] leading-relaxed text-[#3e2723]/70 sm:text-[15px]">
                            {block.intro}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    {features.length ? (
                      <ul className="mt-6 space-y-2.5">
                        {features.map((f, idx) => (
                          <li key={idx} className="flex gap-3 text-[14px] text-[#3e2723]/75">
                            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#b76e79]" aria-hidden />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      <LandscapeCta
        eyebrow={lang === "en" ? "Next step" : "Următorul pas"}
        title={t.services_page.cta_title}
        subtitle={t.services_page.cta_subtitle}
        buttonLabel={t.services_page.cta_button}
        testId="services-bottom-cta-section"
        buttonTestId="services-bottom-cta"
      />
    </main>
  );
}
